"use client";

import type React from "react";
import "chart.js/auto";
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { format } from "date-fns";
import { Icon } from "@iconify/react";
import { useLocale, useTranslations } from "use-intl";
import { getDateFnsIntlObject } from "@/lib/intl/utils";
import { Button } from "@/components/ui/button";
import { Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import type { translations } from "@/messages";
import type { ChartOptions } from "chart.js/auto";
import { getOptionColor } from "@/components/poll-system/helpers";
import type { RouterOutputs } from "@ept/api";
type HoverableCardProps = {
  poll: Omit<RouterOutputs["pollSystemRouter"]["getPolls"]["data"][number], "startDate" | "endDate"> & {
    startDate: string;
    endDate: string;
  };
  userData?: RouterOutputs["auth"]["me"] | undefined;
  onClick?: () => void;
  onShare?: () => void;
};
type TimeLeft = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};
const PollResults = ({
  results,
  showEqualSlices
}: {
  results: {
    title: string;
    percentage: number;
    count: number;
    color: string;
  }[];
  showEqualSlices: boolean;
}) => {
  const data = {
    labels: results.map(result => result.title),
    datasets: [{
      data: showEqualSlices ? Array(results.length).fill(100 / results.length) : results.map(result => result.percentage),
      backgroundColor: results.map(result => result.color),
      borderWidth: 0,
      hoverOffset: 32
    }]
  };
  const options: ChartOptions<"doughnut"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: context => {
            const label = context.label || "";
            const count = results[context.dataIndex]?.count || 0;
            const percentage = context.raw as number;
            return count > 0 ? `${label}: ${count} votes (${percentage.toFixed(2)}%)` : `${label}: ${count} votes`;
          }
        }
      },
      legend: {
        display: false
      }
    },
    cutout: "45%",
    layout: {
      padding: 32
    }
  };
  return <div style={{
    position: "relative",
    width: "100%",
    height: "100%"
  }} data-sentry-component="PollResults" data-sentry-source-file="HoverableCard.tsx">
      <Doughnut data={data} options={options} data-sentry-element="Doughnut" data-sentry-source-file="HoverableCard.tsx" />
    </div>;
};
const HoverableCard: React.FC<HoverableCardProps> = ({
  poll,
  userData,
  onClick,
  onShare
}) => {
  const t = useTranslations();
  const locale = useLocale() as keyof typeof translations;
  const dateFnsIntl = getDateFnsIntlObject(locale);
  const calculateTimeLeft = (): TimeLeft => {
    const difference = new Date(poll.endDate).getTime() - new Date().getTime();
    let timeLeft: TimeLeft = {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    };
    if (difference > 0) {
      timeLeft = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor(difference / (1000 * 60 * 60) % 24),
        minutes: Math.floor(difference / 1000 / 60 % 60),
        seconds: Math.floor(difference / 1000 % 60)
      };
    }
    return timeLeft;
  };
  const calculateTotalDuration = (): number => {
    const start = new Date(poll.startDate).getTime();
    const end = new Date(poll.endDate).getTime();
    return end - start;
  };
  const calculateProgress = (): number => {
    const totalDuration = calculateTotalDuration();
    const timePassed = new Date().getTime() - new Date(poll.startDate).getTime();
    return Math.max(0, Math.min(100, timePassed / totalDuration * 100));
  };
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calculateTimeLeft());
  const [progress, setProgress] = useState<number>(calculateProgress());
  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeLeft(calculateTimeLeft());
      setProgress(calculateProgress());
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);
  const formatTimeLeft = () => {
    const {
      days,
      hours,
      minutes,
      seconds
    } = timeLeft;
    const timeUnits = [{
      unit: "days",
      value: days
    }, {
      unit: "hours",
      value: hours
    }, {
      unit: "minutes",
      value: minutes
    }, {
      unit: "seconds",
      value: seconds
    }];
    const nonZeroUnits = timeUnits.filter(({
      value
    }) => value > 0);
    if (nonZeroUnits.length === 0) return "Poll ended";
    const displayUnits = nonZeroUnits.slice(0, 2).map(({
      unit,
      value
    }) => `${value} ${unit}`);
    return displayUnits.join(" ");
  };
  const totalVotes = poll.pollOptions.reduce((total, option) => total + Number(option.count), 0);
  const pollResults = poll.pollOptions.sort((a, b) => Number(a.displayOrder) - Number(b.displayOrder)).map((option, idx) => ({
    title: option.title,
    percentage: totalVotes > 0 ? Number(option.count) / totalVotes * 100 : 0,
    count: Number(option.count),
    color: getOptionColor(option.title, idx)
  }));
  const showEqualSlices = totalVotes === 0;
  const authorLoggedIn = poll.authorId === userData?.profile?.id;
  return <div className="relative" data-sentry-component="HoverableCard" data-sentry-source-file="HoverableCard.tsx">
      <Card className="flex flex-col gap-2 rounded-3xl p-6 transition-all duration-200 ease-in-out hover:shadow-lg wide:flex-row" data-sentry-element="Card" data-sentry-source-file="HoverableCard.tsx">
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div>
              <div className="mb-3 flex items-center gap-1 opacity-80">
                {poll.isPublic ? <>
                    <Icon icon="ant-design:eye-filled" className="h-4 w-4" />
                    <span className="text-sm font-semibold">
                      {t("src_app_dashboard_polls_view_components_HoverableCard_tsx_0")}
                    </span>
                  </> : <>
                    <Icon icon="ant-design:eye-invisible-outlined" className="h-4 w-4" />
                    <span className="text-sm font-semibold">
                      {t("src_app_dashboard_polls_view_components_HoverableCard_tsx_1")}
                    </span>
                  </>}
              </div>
              <h2 className="text-xl font-semibold">{poll.title}</h2>
              <p className="mt-3 text-xs opacity-60">
                <p className="mt-3 text-xs opacity-60">
                  {t("added_text_20230802124557", {
                  authorDisplayName: !poll.isAnnonymousAuthor ? authorLoggedIn ? t("pollDetails.you") : poll.initiator : `${t("annonymous_user_20230802124558")} ${authorLoggedIn ? `(${t("pollDetails.you")})` : ""}`,
                  formattedStartDate: format(new Date(poll.startDate), "dd MMMM yyyy", {
                    locale: dateFnsIntl
                  }),
                  regionName: poll.region?.name
                })}
                </p>
              </p>
            </div>
          </div>
          <div className="mt-4 flex h-[300px] max-h-[30vh] w-full items-center justify-center wide:hidden">
            <PollResults results={pollResults} showEqualSlices={showEqualSlices} data-sentry-element="PollResults" data-sentry-source-file="HoverableCard.tsx" />
          </div>
          <div className="mt-4">
            <p className="mb-2 text-xs font-semibold">
              {t("src_app_dashboard_polls_view_components_HoverableCard_tsx_3")}
            </p>
            <div className="relative h-8 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div className="h-8 rounded-full bg-green-500 dark:bg-blue-600" style={{
              width: `${progress}%`
            }} />
              <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-foreground/80 shadow-xl dark:text-foreground">
                {formatTimeLeft()}
              </span>
            </div>
          </div>
          <hr className="my-4 opacity-80" />
          <div className="mt-4 flex w-full">
            <Button onClick={onClick} onKeyDown={onClick} className="h-12 flex-1 cursor-pointer bg-blue-700 px-4 text-white hover:bg-blue-800" data-sentry-element="Button" data-sentry-source-file="HoverableCard.tsx">
              <Icon icon="ic:round-how-to-vote" className="mr-2 h-6 w-6" data-sentry-element="Icon" data-sentry-source-file="HoverableCard.tsx" />
              {t("src_app_dashboard_polls_view_components_HoverableCard_tsx_4")}
            </Button>
            <Button className="ml-2 h-12 cursor-pointer bg-card text-foreground/80 hover:bg-card/60 hover:text-foreground" variant="outline" onClick={onShare} onKeyDown={onShare} data-sentry-element="Button" data-sentry-source-file="HoverableCard.tsx">
              <Icon icon="ic:round-share" className="h-6 w-6" data-sentry-element="Icon" data-sentry-source-file="HoverableCard.tsx" />
            </Button>
          </div>
        </div>
        <div className="poll-results-container hidden w-full items-center justify-center overflow-visible wide:flex wide:w-2/5">
          <PollResults results={pollResults} showEqualSlices={showEqualSlices} data-sentry-element="PollResults" data-sentry-source-file="HoverableCard.tsx" />
        </div>
      </Card>
      {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
      <style jsx>{`
        .poll-results-container {
          filter: grayscale(100%);
          transition: filter 0.3s ease-in-out;
        }
        .poll-results-container:hover {
          filter: grayscale(0%);
        }
      `}</style>
    </div>;
};
export default HoverableCard;